import { Center, Loader } from "@mantine/core";
import type { TileRequestTransform } from "@mountaya/elements/editor";
import {
  clampEmbedConfigToSecurity,
  type EmbedConfig,
  type EmbedSafetyRules,
  embedSearchSchema,
  normalizeDefaultProps,
  type ReadyMessage,
  resolveEmbedConfig,
  type StudioToolContext,
} from "@mountaya/studio";
import { type EmbedZoomRange, useStudioHostRegistry } from "@mountaya/studio-react";
import { useStudioToolContext } from "@mountaya/studio-react/headless";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useCallback, useEffect, useMemo } from "react";

import {
  type AuthStrategy,
  organizationLookupQueryOptions,
  organizationQueryOptions,
  SESSION_AUTH,
  type SessionTokenReader,
} from "@mountayaapp/api";
import { type OrganizationBranding, ThemeProvider, toBranding } from "@mountayaapp/theme";

import {
  askFramingPageOf,
  type FramingPageSession,
  holdFramingPageSession,
  isFramed,
} from "../embed/engines/framingPageSession";
import { useFramingPageReread } from "../embed/hooks/useFramingPageReread";
import { createEmbedBridge } from "../embed/rules/bridge";
import { isFramedBySameOrigin } from "../embed/rules/previewFrame";
import { env } from "../env";
import { DEFAULT_ZOOM_MAX, DEFAULT_ZOOM_MIN } from "../iam/organization/rules/securityRules";
import type { RouterContext } from "../routerContext";
import { EmbeddedStudio } from "../studio/components/EmbeddedStudio";
import { buildTileRequestTransform } from "../studio/map/utils/rules/tileRequestTransform";
import { EmbeddedStudioProvider } from "../studio/providers/EmbeddedStudioProvider";

/**
 * EMBED_ZOOM_FALLBACK is the safety zoom range applied when the org's safety_rules
 * leave it unset: the range a new organization starts with, 8 to 18, as the contract
 * states it and the backend writes it (DefaultOrganizationSettings.SafetyRules.ZoomMin /
 * ZoomMax in backend/internal/domain/organization/defaults.go). It is read from the
 * console's own statement of that default, the one the Security page seeds its zoom
 * inputs from, so the embed camera and the page cannot disagree about it. A
 * freshly-provisioned org carries these, but an older or partial settings payload may
 * omit them, so the route backstops the range here before threading it to the surface.
 */
const EMBED_ZOOM_FALLBACK: EmbedZoomRange = {
  zoomMax: DEFAULT_ZOOM_MAX,
  zoomMin: DEFAULT_ZOOM_MIN,
};

/**
 * resolveEmbedZoom reads the org's safety zoom range from settings.safety_rules,
 * falling back to the new-organization default (8-18) for either end the org left
 * unset. It accepts the loosely-typed safety_rules slice both org read shapes expose
 * (the by-id Zod parse types zoom_min/zoom_max as optional numbers; the
 * publishable-key lookup carries the generated required numbers), so a missing slice
 * or a missing end resolves to the fallback rather than NaN.
 */
function resolveEmbedZoom(
  safetyRules: { zoom_max?: number; zoom_min?: number } | undefined,
): EmbedZoomRange {
  return {
    zoomMax: safetyRules?.zoom_max ?? EMBED_ZOOM_FALLBACK.zoomMax,
    zoomMin: safetyRules?.zoom_min ?? EMBED_ZOOM_FALLBACK.zoomMin,
  };
}

/**
 * EmbedSession is the resolved per-request embed identity carried in the ROUTER
 * CONTEXT (never Zustand): the resolved org id (from the publishable-key lookup for
 * a visitor, or by id over the owner's session for a preview) plus the credentials
 * the API embed-auth strategy will use (X-API-Key / X-Session-Token). The Identity
 * store holds only organizationId (anonymous embed → userId null); the publishable
 * key + session token stay here, out of global state. publishableKey is an empty
 * string in a preview with no key selected — the preview authenticates as the owner
 * via SESSION_AUTH, which ignores it.
 *
 * sessionToken is the token the address carried, or, where the framing page renews it, the
 * reader every request and every tile calls for the token current then.
 */
interface EmbedSession {
  organizationId: string;
  publishableKey: string;
  sessionToken?: string | SessionTokenReader;
}

/**
 * EmbedRouteContext is what the embed route's beforeLoad merges into the router
 * context: the resolved embed session and the fully-resolved EmbedConfig. null
 * when the org cannot be resolved — a visitor with a missing/unresolvable
 * publishable key, or a preview whose organization_id is missing or fails to load
 * over the owner's session. The SPA then renders a loader; the host CSP route
 * already 401/403s the document in production, so this is the in-SPA degraded path
 * for a null org.
 */
interface EmbedRouteContext {
  branding: OrganizationBranding | null;
  embedConfig: EmbedConfig | null;
  embedSession: EmbedSession | null;
  /**
   * framingPageSession is the session token the page framing a visitor's embed renews, held
   * where the organization requires one on every read (`require_session_token`) or the address
   * carried one, and the embed is framed. Null otherwise, and always on the owner's preview,
   * which reads under the owner's own session and asks nothing.
   */
  framingPageSession: FramingPageSession | null;
  /**
   * preview is whether this document is the owner-truthful preview: the `preview` flag set
   * AND the document framed by a page of its own origin (isFramedBySameOrigin). It is decided
   * once, here, and the surface's auth strategy reads it rather than the search, so the
   * organization read and the surface reads can never disagree about which credential rides.
   */
  preview: boolean;
  safetyRules?: EmbedSafetyRules;
  /**
   * whiteLabel is whether the publishing organization may suppress the Mountaya mark on
   * this surface. It is resolved HERE because the two paths read it from two different
   * shapes: the publishable-key lookup carries it as a top-level flag (the one read that
   * needs it and must not see an entitlement record), while the owner-truthful preview
   * reads the organization's own entitlement record. Both degrade to false, so the mark
   * stays on wherever the organization could not be resolved.
   */
  whiteLabel: boolean;
  zoomRange: EmbedZoomRange;
}

/**
 * seedEmbedIdentity sets the org-scoped identity (org id, userId left null) and
 * seeds its display preferences from the resolved embed config, so the surface
 * renders in the org's preferred language + units from the first paint. This is a
 * CONSTRUCTION-time bootstrap of the identity store — the i18n bootstrap reads the
 * language and the profile/elevation formatters read the units — that the in-app
 * app likewise performs from the Ory session outside the bus. The in-session
 * Preferences panel (embed-capable) changes these afterward through the bus, so the
 * tool-bus single-mutation-surface invariant still holds for every in-session
 * change; only this first-paint seed runs in the loader. The unit slugs ride as
 * strings (the store does not branch on them); the map label language is read by
 * useMapLanguage.
 *
 * The organization is taken structurally by its id: the owner-preview path resolves the
 * parsed by-id read and the visitor path the wire publishable-key lookup, and the seed
 * needs nothing from either beyond which org this is.
 */
function seedEmbedIdentity(
  context: RouterContext,
  organization: { id: string },
  embedConfig: EmbedConfig,
): void {
  context.identityStore.getState().setOrganization(organization.id);
  context.identityStore.getState().setPreferences({
    language: embedConfig.preferredLanguage,
    length: embedConfig.preferredUnitLength,
    pace: embedConfig.preferredUnitPace,
    slope: embedConfig.preferredUnitSlope,
    temperature: embedConfig.preferredUnitTemperature,
  });
}

/**
 * Route is /studio/embed: the PUBLIC embedded studio. The trailing underscore on
 * the `studio_` segment un-nests it from the _app layout, so it inherits neither
 * the auth guard nor the app shell — an anonymous iframe renders it directly. The
 * root beforeLoad also skips the Ory whoami for this path.
 *
 * It declares neither a `<main>` landmark nor a bypass link, and that is deliberate.
 * `<main>` names a DOCUMENT's dominant content, and this document is a widget inside
 * the customer's page: assistive tech flattens an iframe into the host's landmark
 * list, so a `<main>` here would sit beside — and compete with — the customer's own.
 * There is likewise nothing to bypass, since the surface is one map with no navigation
 * ahead of it; a skip link would be a first tab stop in the customer's page whose
 * destination is where focus already is. The map is a labelled `region` (MapView's
 * ariaLabel), which names this content inside the host's structure without claiming
 * the host's top-level role. The share view, which does put a nav rail ahead of its
 * map, takes the opposite answer and mounts both.
 *
 * beforeLoad resolves the embed contract on one of two paths. A VISITOR (no
 * `preview` flag) looks up the organization from the publishable key (the SPA's
 * first REST call) under the embed auth strategy, so a private itinerary stays
 * gated. The owner-truthful PREVIEW resolves the organization by id over the
 * publisher's own session — no publishable key required — so a member can preview any
 * itinerary they can read, public or private. The flag alone does not make a preview:
 * the document must also be framed by a page of its own origin (isFramedBySameOrigin),
 * which the embed wizard's preview box is and no other page can be. A flag set
 * anywhere else is dropped, and the request takes the visitor path, so public content
 * still renders and nothing is read over the visitor's cookie. The host's embed route
 * refuses such a document before it is served; this holds the rule where that route
 * does not run. Both paths then set the org-scoped identity, seed its display
 * preferences, resolve the EmbedConfig (URL > default_props > fallback), and carry
 * the embed session + config in the router context for the component.
 * validateSearch is the merged three-facet embed schema from core.
 */
export const Route = createFileRoute("/studio_/embed")({
  beforeLoad: async ({ context, search }): Promise<EmbedRouteContext> => {
    if (search.preview === true && isFramedBySameOrigin(window)) {
      // Owner-truthful preview: resolve the org BY ID over the publisher's own
      // session (no publishable key required) so a member can preview any itinerary
      // they can read, public or private. organization_id is the preview-only
      // transport the wizard appends to its own iframe; without it there is no org
      // to resolve, so degrade to the loader.
      const organizationId = search.organization_id;
      if (!organizationId) {
        return {
          branding: null,
          embedConfig: null,
          embedSession: null,
          framingPageSession: null,
          preview: true,
          whiteLabel: false,
          zoomRange: EMBED_ZOOM_FALLBACK,
        };
      }

      try {
        // organizationQueryOptions carries no auth strategy, so the read defaults to
        // the session path (the Ory cookie, credentials: "include"), which is why the
        // frame check above guards this branch. The org read is the membership boundary:
        // an org the publisher cannot read fails here and degrades to the loader.
        const organization = await context.queryClient.ensureQueryData(
          organizationQueryOptions({ baseUrl: env.apiBaseUrl }, organizationId),
        );

        // The by-id org types default_props loosely (the api Zod schema parses the
        // preference fields as plain optional strings), so normalize it to the typed
        // EmbeddedStudioDefaultProps the resolver expects before folding the URL
        // overrides; an org with no default_props falls through to the fallbacks.
        // The safety rules are read twice: the resolver holds the collection's zones to theirs,
        // which the server does not apply to this owner's session, and the clamp the rest.
        const embedConfig = clampEmbedConfigToSecurity(
          resolveEmbedConfig(
            search,
            normalizeDefaultProps(organization.settings.default_props),
            organization.settings.safety_rules,
          ),
          organization.settings.safety_rules,
        );
        seedEmbedIdentity(context, organization, embedConfig);

        // The publishable key may be absent in preview (auth ignores it — the surface
        // authenticates as the owner via SESSION_AUTH). Carry it through as an empty
        // string when none so EmbedSession.publishableKey stays a plain string.
        return {
          branding: toBranding(organization.settings.theme?.colors?.ui),
          embedConfig,
          embedSession: {
            organizationId: organization.id,
            publishableKey: search.publishable_key ?? "",
            sessionToken: search.session_token,
          },
          framingPageSession: null,
          preview: true,
          safetyRules: organization.settings.safety_rules,
          // The organization read answers whether the mark may come off, to every credential
          // that may read it: the one entitlement it carries.
          whiteLabel: organization.white_label,
          zoomRange: resolveEmbedZoom(organization.settings.safety_rules),
        };
      } catch {
        // The org failed to load (not a member / network). Degrade to the loader.
        return {
          branding: null,
          embedConfig: null,
          embedSession: null,
          framingPageSession: null,
          preview: true,
          whiteLabel: false,
          zoomRange: EMBED_ZOOM_FALLBACK,
        };
      }
    }

    const publishableKey = search.publishable_key;
    if (!publishableKey) {
      return {
        branding: null,
        embedConfig: null,
        embedSession: null,
        framingPageSession: null,
        preview: false,
        whiteLabel: false,
        zoomRange: EMBED_ZOOM_FALLBACK,
      };
    }

    try {
      // The lookup is the SPA's first REST call. It rides the embed auth strategy
      // so it sends X-API-Key and (crucially) no Ory cookie cross-origin; the
      // public endpoint reads the publishable_key from the body regardless.
      const organization = await context.queryClient.ensureQueryData(
        organizationLookupQueryOptions(
          {
            auth: { kind: "embed", publishableKey, sessionToken: search.session_token },
            baseUrl: env.apiBaseUrl,
          },
          publishableKey,
        ),
      );

      const embedConfig = clampEmbedConfigToSecurity(
        resolveEmbedConfig(
          search,
          organization.settings.default_props,
          organization.settings.safety_rules,
        ),
        organization.settings.safety_rules,
      );
      seedEmbedIdentity(context, organization, embedConfig);

      // A token lives minutes, and only the integrator's server can mint one, so an embed whose
      // reads carry one asks the page framing it for each renewal, which the snippet's script
      // answers from the integrator's endpoint. The lookup above takes no credential, so it is
      // what says whether the organization requires one. An address that carried no token waits
      // here for the page's first answer, so the first read made under the key carries it. One
      // that carried a token reads with it at once, and reads everything again once the page's
      // first token lands (useFramingPageReread, D58), in case that token was refused.
      const framingPageSession =
        isFramed(window) &&
        (organization.settings.safety_rules.require_session_token ||
          search.session_token !== undefined)
          ? holdFramingPageSession({
              ask: askFramingPageOf(window),
              initialToken: search.session_token,
            })
          : null;
      if (framingPageSession !== null && search.session_token === undefined) {
        await framingPageSession.firstAnswer();
      }

      return {
        branding: toBranding(organization.settings.theme?.colors?.ui),
        embedConfig,
        embedSession: {
          organizationId: organization.id,
          publishableKey,
          sessionToken: framingPageSession?.sessionToken ?? search.session_token,
        },
        framingPageSession,
        preview: false,
        safetyRules: organization.settings.safety_rules,
        // The lookup carries the flag directly: it is the one read that needs it and the
        // one read that must not see an entitlement record.
        whiteLabel: organization.white_label === true,
        zoomRange: resolveEmbedZoom(organization.settings.safety_rules),
      };
    } catch {
      // Lookup failed (unknown/revoked/wrong-scope/network). The host already
      // gates the document; degrade to the loader in the SPA path.
      return {
        branding: null,
        embedConfig: null,
        embedSession: null,
        framingPageSession: null,
        preview: false,
        whiteLabel: false,
        zoomRange: EMBED_ZOOM_FALLBACK,
      };
    }
  },
  component: EmbedRoute,
  validateSearch: embedSearchSchema,
});

/**
 * EmbedSurfaceProps thread the resolved config + the visitor auth strategy to the
 * surface wrapper. The auth strategy builds the tile transform and is passed
 * through for the surface's data reads; the config drives the surface.
 */
interface EmbedSurfaceProps {
  auth: AuthStrategy;
  embedConfig: EmbedConfig;
  /**
   * publishedSurface makes every read paint the PUBLISHED snapshot rather than the
   * draft. The embed is a public surface by definition, so it is always on: an embed
   * exists to show a website's visitors what the organization published, and there is
   * no state in which it should paint work the publisher has not released.
   *
   * The credential alone does not settle it. A bare publishable key reads the
   * publication, and so does a key beside a session token naming nobody, which is the
   * token the hosted embed is told to mint: the API serves it exactly what the key alone
   * would be (ADR 0138). Two callers would read the draft without the flag. The
   * owner-truthful preview authenticates with the owner's session cookie and no key, and
   * the owner reads their draft. A session token naming an active member, a hosted-embed
   * token minted for one by mistake, reaches the organization tier, which reads the draft
   * as well. The flag keeps both on the publication, which is why every embed sets it and
   * not the preview alone.
   *
   * It can only ever narrow: the server's published-surface opt-in is content-only and
   * never widens what a caller is allowed to see.
   */
  publishedSurface?: boolean;
  safetyRules?: EmbedSafetyRules;
  zoomRange: EmbedZoomRange;
}

/**
 * EmbedSurface builds the visitor-truthful tile transform from the embed auth
 * strategy and renders the read-only embedded studio. The single embed strategy
 * authenticates BOTH the surface's data reads (the /v1/tile-layers manifest, the
 * itinerary fetch — passed as the `auth` prop) AND the tile requests (the
 * transform, which the map fetches outside the REST client) — NEVER the Ory cookie
 * — so every read authorizes exactly as a third-party visitor and what the surface
 * paints is what a visitor gets. The transform is built once and keyed on the
 * strategy (MapLibre reads transformRequest at construction; a stable reference
 * keeps MapView from treating it as a changed prop).
 *
 * publishedSurface rides BOTH of those seams together — the transform for the tiles
 * and the surface prop for the itinerary detail — because they render one picture: a
 * published geometry under a draft elevation profile would be worse than either alone.
 * EmbeddedStudio declares it for the dialogs it mounts inside the viewer as well, so the
 * Street View mini-map's tiles paint the same snapshot.
 *
 * It also owns the postMessage bridge, created inside the provider subtree where the
 * bus + tool context are readable through useStudio: the bridge dispatches inbound
 * content-switch messages through the same bus the surface uses (start() returns its
 * own cleanup, so the listener is torn down on unmount), and its postReady is threaded
 * to the surface as onReady so the embed posts the outbound ready event to its host.
 */
function EmbedSurface({
  auth,
  embedConfig,
  publishedSurface,
  safetyRules,
  zoomRange,
}: EmbedSurfaceProps): ReactElement {
  const ctx = useStudioToolContext<StudioToolContext>();
  const { bus } = useStudioHostRegistry();

  const transformRequest = useMemo<TileRequestTransform>(
    () => buildTileRequestTransform({ auth, publishedSurface, tileApiBaseUrl: env.tileApiUrl }),
    [auth, publishedSurface],
  );

  // The bridge is created once and keyed on the bus + tool context so a re-render never
  // re-attaches it; start() attaches the inbound message listener and returns its own
  // cleanup for symmetric teardown.
  const bridge = useMemo(() => createEmbedBridge({ bus, ctx, window }), [bus, ctx]);
  useEffect(() => bridge.start(), [bridge]);

  // Post the outbound ready event through the bridge each time the surface settles; passed
  // to EmbeddedStudio as onReady so the surface never posts to the host directly.
  const onReady = useCallback(
    (payload: Omit<ReadyMessage, "type">) => bridge.postReady(payload),
    [bridge],
  );

  return (
    <EmbeddedStudio
      auth={auth}
      embedConfig={embedConfig}
      onReady={onReady}
      publishedSurface={publishedSurface}
      safetyRules={safetyRules}
      transformRequest={transformRequest}
      zoomRange={zoomRange}
    />
  );
}

function EmbedRoute() {
  // identityStore is the module-scoped store threaded through the router context
  // (the same instance the loader mutated via setOrganization). The provider hands
  // it to the StudioToolContext as ctx.identity.
  // safetyRules rides down to the surface so a host's runtime content switch is
  // re-clamped to the SAME security boundary beforeLoad applied on load. Without it
  // the surface re-clamp degrades to a no-op and a `mountaya:setItinerary` can turn
  // a capability back on that the org locked off in Security.
  //
  // `preview` is the owner-truthful preview signal as beforeLoad decided it: the embed wizard
  // sets the flag on its OWN same-origin preview iframe (never the copyable snippet), and
  // beforeLoad honours it only inside a frame of this origin. It switches the surface's auth
  // strategy below.
  const {
    branding,
    embedConfig,
    embedSession,
    framingPageSession,
    identityStore,
    preview,
    queryClient,
    safetyRules,
    whiteLabel,
    zoomRange,
  } = Route.useRouteContext();

  // The framing page renews the token for as long as the surface is mounted: the engine renews
  // only while something holds it. The token is read per request, so a renewal reaches the next
  // read and the next tile with the map left as it is.
  useEffect(() => framingPageSession?.hold(), [framingPageSession]);

  // An address that carried a token is read with at once, before the framing page answers (D58).
  // Once the page's first token replaces it, every query is read again under it, and the surface
  // is keyed on that answer, so the map is built again and reads its catalogue and its style with
  // the new token: a map never asks again for a catalogue it was refused. A renewal after that
  // rebuilds nothing, as above.
  const rereadDue = useFramingPageReread(framingPageSession, queryClient);

  // The strategy every read on this surface authenticates with — the provider's REST
  // config (ctx.api), the surface's data reads, and the tile transform.
  //
  // - A real embed (and a tokenless preview): the VISITOR strategy — the publishable
  //   key (+ optional session token) ride X-API-Key / X-Session-Token and no cookie,
  //   so the surface authorizes exactly as a third-party visitor and a private
  //   itinerary stays gated. A token the framing page renews is a reader, called for each
  //   request, so the strategy, the client and the tile transform are built once.
  // - The owner-truthful preview (the flag set inside a frame of this origin): the
  //   SESSION strategy — the owner's Ory cookie rides (credentials: "include") and NO
  //   X-API-Key, so the backend authenticates the OWNER (it honours the cookie only
  //   when no X-API-Key is present) and the publisher sees their own itinerary
  //   regardless of privacy. A page of any other origin that sets `preview` on its frame
  //   never reaches this branch: beforeLoad dropped the flag.
  //
  // Built here (above the provider) so the provider's ctx.api carries it; null-safe
  // because the loader returns a null session when the key is unresolvable.
  const auth = useMemo<AuthStrategy | null>(() => {
    if (!embedSession) {
      return null;
    }

    if (preview) {
      return SESSION_AUTH;
    }

    return {
      kind: "embed",
      publishableKey: embedSession.publishableKey,
      sessionToken: embedSession.sessionToken,
    };
  }, [embedSession, preview]);

  if (!embedConfig || !embedSession || !auth) {
    return (
      <Center mih="100vh">
        <Loader />
      </Center>
    );
  }

  // Nest a provider that pins the embed's resolved scheme (light or dark) to the embed
  // subtree, overriding the app shell's light-only root: an org that ships a dark embed
  // renders dark, a light embed light. The embed has no "auto" theme — it lives inside
  // the publisher's page and matches that page's chosen scheme, not the visitor's OS —
  // so the provider always force-pins the scheme (no localStorage slot, no theme
  // switcher). The EmbeddedStudioProvider owns the read-only bus + tool context (the
  // "embedding" capability only, its REST config carrying the visitor auth) and the
  // MapClient lifecycle; the surface mounts the map + display hooks + config-gated
  // controls and owns the bridge, which dispatches inbound content switches through the
  // same bus and posts the outbound ready event to the host.
  //
  // `surface="embedding"` declares Map Publishing's hosted embed to the studio host, and this
  // route is the only place that declares it. The integrator's session token, which a
  // require_session_token organization hands this route, is minted for `api`: a key carrying
  // only `publishing` cannot mint a `studio-embedded` one, and the host's default rule refuses
  // every other, which would leave the embed blank. The hosted embed is recorded by this
  // origin rather than by its session, so its host reads no token and grants the
  // read-only pair alone.
  return (
    <ThemeProvider branding={branding} colorScheme={embedConfig.preferredTheme}>
      <EmbeddedStudioProvider
        auth={auth}
        identityStore={identityStore}
        surface="embedding"
        whiteLabel={whiteLabel}
      >
        {/* Always the published snapshot, preview or not: an embed shows a website's
            visitors what the organization released, and nothing it can be given makes
            the draft the right answer. See EmbedSurfaceProps.publishedSurface for why
            the credential alone does not settle it. */}
        <EmbedSurface
          auth={auth}
          embedConfig={embedConfig}
          key={rereadDue ? "framing-page-token" : "first-token"}
          publishedSurface
          safetyRules={safetyRules}
          zoomRange={zoomRange}
        />
      </EmbeddedStudioProvider>
    </ThemeProvider>
  );
}
